/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import _ from 'lodash';
import React, { ReactComponentElement, RefObject } from 'react';

// first-party
import { AccountModel } from 'src/types/account.model';
import { CardModel } from 'src/types/card.model';
import { FullUserModel } from 'src/types/user.model';
import CashFlowService from '../../../services/cashflow.service';
import FormatService from '../../../services/format.service';
import LiteralsService from '../../../services/literals/LiteralsService';

import BackButton from '../../../components/back-button/BackButton';
import BasicDrawer from './BasicDrawer';
import BWCurrencyInput from 'src/bw/bw-currency-input/BW-Currency-Input';
import CardNameField from '../../../components/card-name-field/CardNameField';
import { Alert, FieldControl, IconSuccess, IconWarning, Label, Select, SelectOption } from '@americanexpress/dls-react';
import MonthPicker from 'src/bw/month-picker/MonthPicker';
import SavingsBottomButtons from './SavingsBottomButtons';
import SavingsGoalAd from '../SavingsGoalAd';
import Typography from '../../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and styles
import './GenericSavingsDrawer.scss';

interface GenericSavingsDrawerState {
    initialRender: boolean;
    accountBalance?: number;
    sharingGoals?: number;
    started: boolean;
    percentSaved?: number;
    amountSaved?: number;
    targetAmount?: number;
    leftToSave?: number;
    perMonth?: number;
    surplus?: number;
    perMonthNeeded?: number;
    savingsAccounts?: AccountModel[];
}

export default class GenericSavingsDrawer extends BasicDrawer<
    any,
    {
        initialRender: boolean;
        started: boolean;
    }
> {
    private readonly accountSelect: RefObject<any> = React.createRef();

    public override state: GenericSavingsDrawerState = {
        initialRender: true,
        started: false
    };

    public static getDerivedStateFromProps(props: any): any {
        return GenericSavingsDrawer.calcSavings(props);
    }

    private static calcSavings(props: Readonly<any>): any {
        const drawerProps: any = props.drawerProps;
        const card: CardModel = drawerProps.card;
        const user: FullUserModel = props.user;
        const started: boolean = _.has(card.attributes, 'started');
        const accounts: AccountModel[] = _.get(props, 'user.accounts');
        const savingsAccounts = _.filter(
            accounts,
            (account: AccountModel) =>
                account.subtype === 'savings' || account.subtype === 'brokerage' || account.subtype === 'checking'
        );
        let account: any;
        let leftToSave: number = card.attributes.targetBalance;
        const months: number = card.startDate.until(card.endDate).toTotalMonths();
        const surplus: number = GenericSavingsDrawer.getMonthlyAvailableForCard(card);
        let perMonthNeeded = Math.ceil(Math.max(0, leftToSave / months));
        let accountBalance: number;
        let sharingGoals: number;

        const accountId: number = card.accountId;
        if (accountId && savingsAccounts) {
            account = _.find(savingsAccounts, { id: accountId });
            const goals: CardModel[] = _.filter(props.user.cards, { accountId });
            sharingGoals = goals.length;
            const currentCard: CardModel = _.find(user.cards, { id: card.id });
            if (!currentCard) {
                return;
            }
            if (currentCard.accountId !== card.accountId) {
                sharingGoals++;
            }

            if (account) {
                accountBalance = account.current;
                leftToSave = Math.max(0, leftToSave - account.current / sharingGoals);
                // recalc amount needed per month
                perMonthNeeded = Math.ceil(Math.max(0, leftToSave / months));
                if (!_.isNumber(card.attributes.perMonth)) {
                    // if the field hasn't been set yet, this is the default
                    props.handleChangeCardAttribute(card, perMonthNeeded, 'perMonth', 'number');
                }
            }
        }

        let percentSaved: number;
        let amountSaved: number;
        const target: number = _.get(card, 'attributes.targetBalance');
        if (account && started) {
            // TODO this may not work when an account is shared by multiple savings cards
            amountSaved = Math.min(account.current, target);
            percentSaved = amountSaved ? Math.round((100 * amountSaved) / target) : 0;
        }

        return {
            accountBalance,
            sharingGoals,
            started,
            percentSaved,
            amountSaved,
            targetAmount: target,
            leftToSave,
            perMonth: card.attributes.perMonth,
            surplus,
            perMonthNeeded,
            savingsAccounts
        };
    }

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;
        const card: CardModel = drawerProps.card;

        const startLabel = 'When do you want to start saving?';
        let accountTotalDiv: JSX.Element = null;
        let status: JSX.Element = null;
        let progress: JSX.Element = null;

        const backButton: JSX.Element = (
            <div className="back-button-container">
                <BackButton drawerProps={drawerProps} handleBack={this.props.handleBack} />
            </div>
        );

        const cardNameField: JSX.Element = (
            <div className="card-name-container">
                <CardNameField
                    key="card-name-field"
                    card={drawerProps.card}
                    handleChangeCard={this.props.handleChangeCard}
                />
            </div>
        );

        if (_.isNumber(this.state.accountBalance)) {
            if (this.state.sharingGoals) {
                accountTotalDiv = (
                    <Typography variant={TypographyVariants.CAPTION} className="account-subtext after24">
                        Account balance: {FormatService.format(this.state.accountBalance)} shared between{' '}
                        {this.state.sharingGoals} {this.state.sharingGoals === 1 ? 'goal' : 'goals'}
                    </Typography>
                );
            }
            if (this.state.started) {
                const styles: any = {};
                if (this.state.initialRender) {
                    styles.width = '0%';
                    window.setTimeout(() => this.setState({ initialRender: false }), 500);
                } else {
                    styles.width = `${this.state.percentSaved}%`;
                    if (this.state.percentSaved > 99) styles.borderRadius = '4px';
                }

                progress = (
                    <React.Fragment>
                        <Typography variant={TypographyVariants.HEADING_M} key="delta" className="delta">
                            {FormatService.format(this.state.amountSaved)} saved
                        </Typography>
                        <Typography variant={TypographyVariants.CAPTION} key="delta2" className="delta2">
                            {LiteralsService.getLiteral('SavingsDrawer.percentSaved', [this.state.percentSaved])}
                        </Typography>
                        <div className="status-bar">
                            <div className="progress-bar" />
                            <div className="progress" style={styles} />
                        </div>
                        <Typography variant={TypographyVariants.BODY} key="zero" className="left">
                            $0
                        </Typography>
                        <Typography variant={TypographyVariants.BODY} key="max" className="right">
                            {FormatService.format(this.state.targetAmount)}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.SUBTEXT}
                            key="funds-title"
                            className="details-title section-line after24"
                        >
                            Savings goal details
                        </Typography>
                    </React.Fragment>
                );
            }
        }

        if (!_.isNumber(this.state.leftToSave)) {
            status = (
                <Typography variant={TypographyVariants.BODY} className="looks-bad before24 after24" key="looks-bad">
                    Enter a savings goal.
                </Typography>
            );
        } else if (this.state.perMonth > 0) {
            if (this.state.leftToSave > 0) {
                if (this.state.perMonth > this.state.surplus) {
                    status = (
                        <Typography
                            variant={TypographyVariants.SUBTEXT}
                            className="looks-bad before24 after24"
                            key="looks-bad"
                        >
                            <IconWarning /> {LiteralsService.getLiteral('SavingsDrawer.notComfortableSave')}
                        </Typography>
                    );
                } else {
                    if (this.state.perMonth >= this.state.perMonthNeeded) {
                        status = (
                            <Typography
                                variant={TypographyVariants.SUBTEXT}
                                className="looks-good before24 after24"
                                key="looks-good"
                            >
                                <IconSuccess /> {LiteralsService.getLiteral('SavingsDrawer.onTime')}
                            </Typography>
                        );
                    } else {
                        status = (
                            <Typography
                                variant={TypographyVariants.SUBTEXT}
                                className="looks-bad before24 after24"
                                key="looks-bad"
                            >
                                <IconWarning /> {LiteralsService.getLiteral('SavingsDrawer.notOnTime')}
                            </Typography>
                        );
                    }
                }
            } else {
                status = (
                    <Typography
                        variant={TypographyVariants.SUBTEXT}
                        className="looks-good before24 after24"
                        key="looks-good"
                    >
                        <IconSuccess /> {LiteralsService.getLiteral('SavingsDrawer.met')}
                    </Typography>
                );
            }
        }

        const savingsInfoArea: JSX.Element = (
            <div className="savings-info-container">
                <Typography variant={TypographyVariants.SUBTEXT} className="amount-label" component="div">
                    Your savings goal
                </Typography>
                <Typography variant={TypographyVariants.HEADING_L} className="amount-text" component="div">
                    {FormatService.format(this.state.targetAmount) || FormatService.format(0)}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT} className="selected-date" component="div">
                    {this.props.costText || 'Total cost'}
                </Typography>
                <Typography
                    variant={TypographyVariants.SUBTEXT}
                    onClick={() => this.props.handleOpenLinkedCard(card)}
                    className="change-this-link link"
                >
                    {LiteralsService.getLiteral('SavingsDrawer.change')}
                </Typography>
                <br />
                <MonthPicker
                    date={drawerProps.card.startDate}
                    mode="future-only"
                    endYear={CashFlowService.getLastCashFlow(this.props.user.cashFlow).startDate.year()}
                    handleChangeDate={this.props.handleChangeCardDate}
                    whichDate="startDate"
                    label={startLabel}
                    className="lone-picker"
                />
            </div>
        );

        const accountSelector: JSX.Element = (
            <FieldControl className="account-selector-container">
                <Label htmlFor="accountId" id="accountIdLabel">
                    What account do you want to save to?
                </Label>
                <Select
                    id="accountId"
                    value={card.accountId ? card.accountId : undefined}
                    onChange={(e: any) => {
                        this.handleAccount(e);
                    }}
                    required
                    fluid
                    ref={this.accountSelect}
                >
                    <SelectOption />
                    {GenericSavingsDrawer.getAccountOptions(this.state.savingsAccounts)}
                </Select>
                <Alert>No savings account selected</Alert>
            </FieldControl>
        );

        const savingsGoalAd: JSX.Element = (
            <div className="savings-goal-ad before24">
                <SavingsGoalAd cardType={card.description} />
            </div>
        );

        const howMuchPerMonth: JSX.Element = card.accountId ? (
            <div className="per-month-container">
                <Typography variant={TypographyVariants.SUBTEXT} className="monthly-label" component="div">
                    {LiteralsService.getLiteral('SavingsDrawer.savePerMonth')}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT} className="total-to-save" component="div">
                    You need to save {FormatService.format(this.state.leftToSave)} total.
                </Typography>
                <Typography variant={TypographyVariants.CAPTION} className="monthly-subtext" component="div">
                    {LiteralsService.getLiteral('SavingsDrawer.perMonthNeeded', [
                        FormatService.format(this.state.perMonthNeeded),
                        FormatService.format(Math.max(this.state.surplus, 0))
                    ])}
                </Typography>
                <BWCurrencyInput
                    fullWidth={true}
                    key="monthly"
                    className="monthly monthly-currency-input"
                    defaultValue={0}
                    status=""
                    value={Math.max(0, this.state.perMonth)}
                    showFractions={false}
                    onChange={(value: number) => {
                        this.props.handleChangeCardAttribute(card, value || 0, 'perMonth', 'number');
                    }}
                />
            </div>
        ) : null;

        const bottomButtons: JSX.Element = (
            <SavingsBottomButtons
                disabled={!card.attributes.targetBalance}
                handleUpdateCard={this.props.handleUpdateCard}
                drawerProps={drawerProps}
                handleDeleteCard={this.props.handleDeleteCard}
                canSubmit={GenericSavingsDrawer.validateFields.bind(this, card, this.accountSelect)}
                afterUpdateAction={this.afterUpdateAction.bind(this)}
            />
        );

        return (
            <div className="savings-generic-goal-drawer drawer">
                <div className="savings-header">
                    {backButton}
                    {cardNameField}
                </div>
                {progress}
                {savingsInfoArea}
                {accountSelector}
                {accountTotalDiv}
                {savingsGoalAd}
                {howMuchPerMonth}
                {card.accountId && this.state.perMonth ? status : null}
                {bottomButtons}
            </div>
        );
    }

    private handleAccount(e: any): void {
        e.target.blur();
        const accountId = Number(e.target.value);

        // TODO I don't know why changing the card doesn't also change the one in user.cards
        // update the card in the user list (separate copy of the current card)
        const userCard = _.find(this.props.user.cards, { id: this.props.drawerProps.card.id });
        userCard.accountId = accountId;

        // update the card
        this.props.handleChangeCard(this.props.drawerProps.card, accountId, 'accountId');
    }

    private static validateFields(card: CardModel, accountSelect: RefObject<any>): boolean {
        // check whether an account has been selected
        if (!card.accountId) {
            accountSelect.current.focus();
            accountSelect.current.blur();
            return false;
        }
        return true;
    }

    /** Get the monthlyAvailable available to save from the extended/edit card payload */
    private static getMonthlyAvailableForCard(card: CardModel): number {
        const startDate: string = card.startDate.toJSON();
        const endDate: string = card.endDate.toJSON();

        const filtered = _.filter(_.toPairs((card as any).monthlyNetIncome), (tuple: any) => {
            return tuple[0] >= startDate && tuple[0] < endDate;
        });
        return _.meanBy(filtered, (tuple: any) => {
            return tuple[1];
        });
    }

    private static getAccountOptions(accounts: AccountModel[]): JSX.Element[] {
        if (accounts == null) return null;
        return accounts.map((account: AccountModel) => {
            return (
                <SelectOption key={account.id} value={account.id}>
                    {account.name}
                </SelectOption>
            );
        });
    }

    private async afterUpdateAction(): Promise<void> {
        this.props.handleBack();
    }
}
